import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import * as d3 from "d3";
import { fitAxisLabel } from "./moonlake-axis-labels";

// ===========================================================================
// 100% stacked bars that open into a waterfall
//
// Ported from DUcharts' waterfall-chart (components/waterfall-chart). The
// drawing is the same — stacked bars view with a brush below and a y-axis zoom
// slider, and a click on a bar opens that bar as a staircase with extension
// boxes — but it is driven by inputs instead of a service, so several cards on
// one page can each own an instance. The legend lives in the card header (HTML),
// so this component takes the hidden keys rather than drawing its own legend.
// ===========================================================================

export interface WaterfallRow {
  name: string;
  total: number;
  [key: string]: number | string;
}

export interface WaterfallConfig {
  /** Stack order, bottom → top. */
  barKeys: string[];
  /**
   * Reading order for lists (the bar tooltip) when it differs from the stack
   * order — e.g. 1L, 2L, 3L, 4L … while the deep lines stack deepest first.
   * Defaults to barKeys.
   */
  displayKeys?: string[];
  colours: Record<string, string>;
  legendLabels?: Record<string, string>;
  shortLabels?: Record<string, string>;
  /** Optional explicit top → bottom gradient for a key (e.g. the grouped 3L+). */
  gradients?: Record<string, [string, string]>;
  xAxisLabel: string;
  yAxisLabel: string;
  chartHeight: number;
  /** Unit noun for tooltips and the waterfall summary ("lives"). */
  unit: string;
  valueFormat: (v: number) => string;
  /** "pct" (default): 100% stacked. "abs": stacked absolute values. */
  yMode?: "pct" | "abs";
  noDataMessage?: string;
}

interface DerivedColors {
  solid: string;
  accent: string;
  gradient: [string, string];
  extBg: string;
  extBorder: string;
}

interface SegmentData extends DerivedColors {
  key: string;
  gradId: string;
  label: string;
  shortLabel: string;
  count: number;
  pct: number;
  pctRound: number;
}

type ChartView = "bars" | "waterfall";

const FONT = "Poppins";
const AXIS_LINE = "#d1d5db";
const GRID_LINE = "#e5e7eb";
const AXIS_LABEL = "#101d42";
const TICK_LABEL = "#101d42";
const COUNT_LABEL = "#111827";
const SLIDER_TRACK = "#e5e7eb";
const SLIDER_ACCENT = "#1363df";
// Brush selection — the shared line chart's values.
const BRUSH_FILL = "#A0A0A0";
const BRUSH_STROKE = "#E8EAEE";

let nextId = 0;

@Component({
  selector: "app-moonlake-waterfall-chart",
  templateUrl: "./moonlake-waterfall-chart.component.html",
  styleUrls: ["./moonlake-waterfall-chart.component.scss"],
  standalone: false,
})
export class MoonlakeWaterfallChartComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() data: WaterfallRow[] = [];
  @Input() config!: WaterfallConfig;
  @Input() hiddenKeys: Set<string> = new Set();
  /** Reports which bar is open (its name), or null when back on the bars. */
  @Output() viewChange = new EventEmitter<string | null>();

  @ViewChild("chartEl", { static: true }) chartEl!: ElementRef<HTMLElement>;

  /** Unique per instance: every d3 selection below is scoped by it. */
  readonly divId = `mlwf${nextId++}`;

  currentView: ChartView = "bars";
  waterfallIndex: number | null = null;
  handleRadius = 6;
  lineMin = 0;
  lineMax = 100;

  private colors: Record<string, DerivedColors> = {};
  private gradIds: Record<string, string> = {};
  private viewReady = false;
  private resizeObs?: ResizeObserver;
  private resizeFrame = 0;
  private lastWidth = 0;

  get waterfallName(): string {
    return this.waterfallIndex !== null && this.data[this.waterfallIndex]
      ? String(this.data[this.waterfallIndex].name)
      : "";
  }

  // ── Tooltip element ──
  private tooltipEl: HTMLDivElement | null = null;
  private boundMouseMove = (e: MouseEvent) => {
    if (this.tooltipEl?.classList.contains("show")) this.positionTooltip(e);
  };

  constructor(private zone: NgZone) {}

  // ── Lifecycle ──
  ngAfterViewInit(): void {
    this.tooltipEl = document.createElement("div");
    this.tooltipEl.className = "mlwf-tt";
    document.body.appendChild(this.tooltipEl);

    // d3 work never needs change detection; keep its listeners out of the zone.
    this.zone.runOutsideAngular(() => {
      document.addEventListener("mousemove", this.boundMouseMove);
      this.initiateCharts();
      this.viewReady = true;
      this.redraw();

      // Re-plot on any width change — window resize, the card going fullscreen,
      // or the scroll region gaining/losing its scrollbar.
      this.resizeObs = new ResizeObserver((entries) => {
        const w = Math.round(entries[0]?.contentRect.width || 0);
        if (!w || w === this.lastWidth) return;
        this.lastWidth = w;
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.redraw());
      });
      this.resizeObs.observe(this.chartEl.nativeElement);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (changes["data"] && this.currentView === "waterfall") {
      // Keep the open bar open across a data change if it still exists.
      const name = changes["data"].previousValue?.[this.waterfallIndex ?? -1]?.name;
      const idx = this.data.findIndex((r) => r.name === name);
      if (idx >= 0) {
        this.waterfallIndex = idx;
      } else {
        this.setView("bars", null);
      }
    }
    this.zone.runOutsideAngular(() => this.redraw());
  }

  ngOnDestroy(): void {
    document.removeEventListener("mousemove", this.boundMouseMove);
    this.resizeObs?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
    this.tooltipEl?.remove();
    this.tooltipEl = null;
  }

  /** Back to the stacked bars (the toolbar's Back button). */
  showBarsView(): void {
    const svg = d3.select("#svg_" + this.divId);
    this.hideTooltip();
    svg
      .transition()
      .duration(150)
      .style("opacity", 0)
      .on("end", () => {
        this.zone.run(() => this.setView("bars", null));
        this.redraw();
        svg.transition().duration(250).style("opacity", 1);
      });
  }

  private showWaterfall(idx: number): void {
    const svg = d3.select("#svg_" + this.divId);
    this.hideTooltip();
    svg
      .transition()
      .duration(150)
      .style("opacity", 0)
      .on("end", () => {
        this.zone.run(() => this.setView("waterfall", idx));
        this.redraw();
        svg.transition().duration(250).style("opacity", 1);
      });
  }

  private setView(view: ChartView, idx: number | null): void {
    this.currentView = view;
    this.waterfallIndex = idx;
    this.viewChange.emit(view === "waterfall" ? this.waterfallName : null);
  }

  private redraw(): void {
    if (!this.viewReady || !this.config) return;
    this.buildColors();
    this.createGradientDefs();
    if (this.currentView === "waterfall" && this.waterfallIndex !== null) {
      this.plotWaterfallChart();
    } else {
      this.plotChart();
    }
  }

  // ══════════════════════════════════════════════════════
  // COLOR DERIVATION — single hex → full color set
  // ══════════════════════════════════════════════════════
  private deriveColors(hex: string, gradient?: [string, string]): DerivedColors {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const darken = (c: number) =>
      Math.round(c * 0.85).toString(16).padStart(2, "0");
    return {
      solid: hex,
      accent: hex,
      gradient: gradient || [hex, "#" + darken(r) + darken(g) + darken(b)],
      extBg: `rgba(${r},${g},${b},0.25)`,
      extBorder: `rgba(${r},${g},${b},0.4)`,
    };
  }

  private buildColors(): void {
    this.colors = {};
    this.gradIds = {};
    this.config.barKeys.forEach((key, i) => {
      this.colors[key] = this.deriveColors(
        this.config.colours[key] || "#999999",
        this.config.gradients?.[key]
      );
      // Keys carry spaces and "+", which are not valid in url(#…) references.
      this.gradIds[key] = `grad_${i}_${this.divId}`;
    });
  }

  // ══════════════════════════════════════════════════════
  // SEGMENT HELPERS
  // ══════════════════════════════════════════════════════
  private sumOf(row: WaterfallRow, keys: string[]): number {
    return keys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0);
  }

  private visibleKeys(): string[] {
    return this.config.barKeys.filter((k) => !this.hiddenKeys.has(k));
  }

  private getSegments(row: WaterfallRow, keys: string[]): SegmentData[] {
    const total = this.sumOf(row, keys);
    return keys.map((key) => {
      const count = Number(row[key]) || 0;
      return {
        key,
        gradId: this.gradIds[key],
        label: this.config.legendLabels?.[key] || key,
        shortLabel: this.config.shortLabels?.[key] || key,
        ...this.colors[key],
        count,
        pct: total > 0 ? count / total : 0,
        pctRound: total > 0 ? Math.round((count / total) * 100) : 0,
      };
    });
  }

  private fmt(v: number): string {
    return this.config.valueFormat(v);
  }

  private get isAbs(): boolean {
    return this.config.yMode === "abs";
  }

  /** Top of the y domain: 100 for the % view, a rounded-up max otherwise. */
  private yMaxFor(maxValue: number): number {
    if (!this.isAbs) return 100;
    if (!(maxValue > 0)) return 1;
    return d3.scaleLinear().domain([0, maxValue]).nice().domain()[1] as number;
  }

  private tickFormat(d: number): string {
    return this.isAbs ? this.fmt(d) : Math.round(d) + "%";
  }

  // ══════════════════════════════════════════════════════
  // GRADIENT DEFS
  // ══════════════════════════════════════════════════════
  private createGradientDefs(): void {
    const defs = d3.select("#svg_" + this.divId).select("defs");
    defs.selectAll("linearGradient").remove();
    this.config.barKeys.forEach((key) => {
      const c = this.colors[key];
      const grad = defs
        .append("linearGradient")
        .attr("id", this.gradIds[key])
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "0%")
        .attr("y2", "100%");
      grad.append("stop").attr("offset", "0%").attr("stop-color", c.gradient[0]);
      grad.append("stop").attr("offset", "100%").attr("stop-color", c.gradient[1]);
    });
  }

  // ══════════════════════════════════════════════════════
  // TOOLTIP
  // ══════════════════════════════════════════════════════
  private showTooltip(type: "bar" | "segment", d: any, event: MouseEvent): void {
    if (!this.tooltipEl) return;
    const unit = this.config.unit;
    let html = "";
    if (type === "bar") {
      const row: WaterfallRow = d.row;
      // Listed in reading order (1L, 2L, 3L, …), not stack order.
      const order = (this.config.displayKeys || this.config.barKeys).filter((k) =>
        this.config.barKeys.includes(k)
      );
      const segs = this.getSegments(row, order);
      const shown = this.sumOf(row, this.visibleKeys());
      const rows = segs
        .map((s) => {
          const dim = this.hiddenKeys.has(s.key) ? "opacity:.35;" : "";
          return `<div class="tt-r" style="${dim}"><span class="tt-d" style="background:${s.solid}"></span><span class="tt-l">${s.label}</span><span class="tt-v">${this.fmt(s.count)}</span><span class="tt-p">${s.pctRound}%</span></div>`;
        })
        .join("");
      html = `<div class="tt-hd">${row.name} — ${this.fmt(shown)} ${unit}</div><div class="tt-body">${rows}</div>`;
    } else {
      const row: WaterfallRow = d.row;
      const seg: SegmentData = d.seg;
      html = `<div class="tt-hd">${row.name} — ${seg.label}</div><div class="tt-body">
        <div class="tt-r"><span class="tt-d" style="background:${seg.solid}"></span><span class="tt-l">${unit[0].toUpperCase() + unit.slice(1)}</span><span class="tt-v">${this.fmt(seg.count)}</span></div>
        <div class="tt-r"><span class="tt-d" style="background:${seg.accent}"></span><span class="tt-l">Share</span><span class="tt-v">${seg.pctRound}%</span></div></div>`;
    }
    this.tooltipEl.innerHTML = html;
    this.tooltipEl.classList.add("show");
    this.positionTooltip(event);
  }

  private hideTooltip(): void {
    this.tooltipEl?.classList.remove("show");
  }

  private positionTooltip(e: MouseEvent): void {
    if (!this.tooltipEl) return;
    const r = this.tooltipEl.getBoundingClientRect();
    const gap = 16; // room for the 7px arrow plus a little air
    // Beside the cursor, vertically centred on it so the arrow points at it;
    // flipped to the left near the window's right edge.
    const flip = e.clientX + gap + r.width > window.innerWidth - 10;
    const x = flip ? e.clientX - r.width - gap : e.clientX + gap;
    const y = Math.min(
      Math.max(10, e.clientY - r.height / 2),
      window.innerHeight - r.height - 10
    );
    this.tooltipEl.classList.toggle("tt-at-left", flip);
    this.tooltipEl.classList.toggle("tt-at-right", !flip);
    this.tooltipEl.style.left = x + "px";
    this.tooltipEl.style.top = y + "px";
  }

  // ══════════════════════════════════════════════════════
  // STEP 1: initiateCharts() — One-Time SVG Scaffolding
  // ══════════════════════════════════════════════════════
  private initiateCharts(): void {
    const myClass = this.divId;

    const mySvg = d3
      .select(this.chartEl.nativeElement)
      .append("svg")
      .attr("id", "svg_" + myClass)
      .attr("width", "100%")
      .style("background-color", "white");

    mySvg.append("text").attr("id", "noDataMessage" + myClass);

    const defs = mySvg.append("defs");
    defs
      .append("clipPath")
      .attr("id", "brushClip" + myClass)
      .append("rect")
      .attr("id", "brushClipRect" + myClass);
    defs
      .append("clipPath")
      .attr("id", "xAxisClip" + myClass)
      .append("rect")
      .attr("id", "xAxisClipRect" + myClass);

    // Drop-shadow filter for brush handles
    const filter = defs
      .append("filter")
      .attr("id", "drop-shadow-brush-" + myClass)
      .attr("width", "200%")
      .attr("height", "200%");
    filter
      .append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 1)
      .attr("result", "blur");
    filter
      .append("feOffset")
      .attr("in", "blur")
      .attr("dx", 1)
      .attr("dy", 1)
      .attr("result", "offsetBlur");
    filter
      .append("feFlood")
      .attr("in", "offsetBlur")
      .attr("flood-color", "#000000")
      .attr("flood-opacity", 0.4)
      .attr("result", "offsetColor");
    filter
      .append("feComposite")
      .attr("in", "offsetColor")
      .attr("in2", "offsetBlur")
      .attr("operator", "in")
      .attr("result", "offsetBlur");
    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "offsetBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    mySvg.append("g").attr("id", "yAxis" + myClass).attr("class", "axis" + myClass);
    mySvg.append("g").attr("id", "xAxis" + myClass).attr("class", "axis" + myClass);
    mySvg.append("text").attr("id", "yAxisLabel" + myClass);
    mySvg.append("text").attr("id", "xAxisLabel" + myClass);
    mySvg.append("g").attr("id", "chartGroup" + myClass);
    mySvg.append("g").attr("id", "brushGroup" + myClass);

    ["handleLeftLine1", "handleLeftLine2", "handleRightLine1", "handleRightLine2"].forEach(
      (c) =>
        mySvg
          .append("line")
          .attr("class", "handleLines" + myClass + " " + c + myClass)
    );

    mySvg.append("g").attr("id", "sliderGroup" + myClass).attr("class", "slider" + myClass);
  }

  // ══════════════════════════════════════════════════════
  // Y-axis zoom slider — shared by both views
  // ══════════════════════════════════════════════════════
  // The handles always move in 0–100 (percent of the axis); `yMax` maps that
  // onto the axis' own units, so the same slider zooms % and absolute views.
  private createRangeSlider(
    margins: any,
    chartHeight: number,
    targetScale: any,
    yMax: number,
    onChange: () => void
  ): void {
    const myChart = this;
    const id = "primary";
    const min = 0;
    const max = 100;
    const slider = d3.select("#svg_" + this.divId).select("#sliderGroup" + this.divId);
    slider.selectAll("*").remove();
    const sliderGroup: any = slider.append("g").attr("id", "sliderGroup-" + id);
    const sliderClass = id + "-slider";
    const xPos = margins.left;

    const scale = d3.scaleLinear().domain([min, max]).range([chartHeight, 0]).clamp(true);

    const onDrag = (event: any, handleType: "min" | "max") => {
      const pos = event.y - margins.top;
      const gap = Math.max(1, (max - min) * 0.05);
      if (handleType === "min") {
        const clampedPos = Math.min(scale(min), Math.max(scale(max), pos));
        const newMin = scale.invert(clampedPos);
        if (newMin >= min && newMin <= myChart.lineMax - gap) {
          myChart.lineMin = newMin;
          moveHandle("min", clampedPos);
        }
      } else {
        const clampedPos = Math.max(scale(max), Math.min(scale(min), pos));
        const newMax = scale.invert(clampedPos);
        if (newMax <= max && newMax >= myChart.lineMin + gap) {
          myChart.lineMax = newMax;
          moveHandle("max", clampedPos);
        }
      }
      sliderGroup
        .select("." + id + "-track-inset")
        .attr("y1", scale(myChart.lineMin))
        .attr("y2", scale(myChart.lineMax));
      targetScale.domain([(myChart.lineMin * yMax) / 100, (myChart.lineMax * yMax) / 100]);
      onChange();
    };

    const moveHandle = (handleType: string, pos: number) => {
      sliderGroup.select("." + id + "-" + handleType + "-handle").attr("cy", pos);
      sliderGroup.select("." + id + "-" + handleType + "-hover-layer").attr("cy", pos);
    };

    sliderGroup
      .append("line")
      .attr("class", sliderClass + " " + id + "-track")
      .attr("y1", scale(min))
      .attr("y2", scale(max))
      .attr("transform", "translate(" + xPos + "," + margins.top + ")")
      .attr("stroke", SLIDER_TRACK)
      .attr("stroke-width", 3)
      .attr("stroke-linecap", "round");

    sliderGroup
      .append("line")
      .attr("class", sliderClass + " " + id + "-track-inset")
      .attr("y1", scale(this.lineMin))
      .attr("y2", scale(this.lineMax))
      .attr("transform", "translate(" + xPos + "," + margins.top + ")")
      .attr("stroke", SLIDER_ACCENT)
      .attr("stroke-width", 3)
      .attr("stroke-linecap", "round");

    (["min", "max"] as const).forEach((handleType) => {
      const value = handleType === "min" ? this.lineMin : this.lineMax;
      const hoverLayer = sliderGroup
        .append("circle")
        .attr("class", sliderClass + " " + id + "-" + handleType + "-hover-layer")
        .attr("cy", scale(value))
        .attr("transform", "translate(" + xPos + "," + margins.top + ")")
        .attr("r", this.handleRadius + 8)
        .attr("fill", SLIDER_ACCENT)
        .attr("opacity", 0)
        .attr("pointer-events", "none");

      const handle = sliderGroup
        .append("circle")
        .attr("class", sliderClass + " " + id + "-" + handleType + "-handle")
        .attr("cy", scale(value))
        .attr("transform", "translate(" + xPos + "," + margins.top + ")")
        .attr("r", this.handleRadius)
        .attr("fill", SLIDER_ACCENT)
        .attr("stroke", "white")
        .attr("stroke-width", 2)
        .style("cursor", "ns-resize")
        .on("mousedown", () => handle.raise())
        .call(d3.drag().on("drag", (event: any) => onDrag(event, handleType)));

      handle
        .on("mouseover", () => hoverLayer.transition().duration(200).attr("opacity", 0.4))
        .on("mouseout", () => hoverLayer.transition().duration(400).attr("opacity", 0));
    });
  }

  // ══════════════════════════════════════════════════════
  // STEP 2: plotChart() — 100% Stacked Bars View
  // ══════════════════════════════════════════════════════
  private plotChart(): void {
    const myChart = this;
    const myClass = myChart.divId;
    const props = myChart.config;
    const mySvg: any = d3.select("#svg_" + myClass);
    mySvg.attr("width", "100%");
    const width: number = mySvg.node().getBoundingClientRect().width;
    const height = props.chartHeight;

    mySvg.attr("height", height);

    const margins: any = { left: 64, right: 20, top: 24, bottom: 6, mid: 62, brush: 52 };
    const chartHeight = height - margins.top - margins.bottom - margins.mid - margins.brush;
    const chartWidth = width - margins.left - margins.right;
    const rows: WaterfallRow[] = myChart.data || [];
    const visibleKeys = myChart.visibleKeys();
    const empty = rows.length === 0;

    mySvg
      .select("#noDataMessage" + myClass)
      .attr("x", margins.left + chartWidth / 2)
      .attr("y", margins.top + chartHeight / 2)
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "14px")
      .attr("font-weight", "500")
      .attr("text-anchor", "middle")
      .text(empty ? props.noDataMessage || "There is no data for this selection" : "");

    // Process data for stacking — y0/y1 in percent, or in lives for "abs".
    const abs = myChart.isAbs;
    const stackData = rows.map((row, i) => {
      const segs = myChart.getSegments(row, visibleKeys);
      let cum = 0;
      return {
        row,
        index: i,
        segments: segs.map((s) => {
          const y0 = cum;
          cum += abs ? s.count : s.pct * 100;
          return { ...s, y0, y1: cum };
        }),
      };
    });
    const yMax = myChart.yMaxFor(
      d3.max(stackData, (d) => d.segments[d.segments.length - 1]?.y1 || 0) || 0
    );

    const xScaleAll: any = d3.scaleLinear().domain([0, rows.length]).range([0, chartWidth]);
    const xScale: any = d3.scaleLinear().domain([0, rows.length]).range([0, chartWidth]);
    const yScale: any = d3
      .scaleLinear()
      .domain([(myChart.lineMin * yMax) / 100, (myChart.lineMax * yMax) / 100])
      .range([chartHeight, 0]);

    mySvg
      .select("#xAxis" + myClass)
      .attr("visibility", "visible")
      .attr("transform", "translate(" + margins.left + "," + (margins.top + chartHeight) + ")")
      .attr("clip-path", "url(#xAxisClip" + myClass + ")");

    mySvg
      .select("#yAxisLabel" + myClass)
      .attr("visibility", empty ? "hidden" : "visible")
      .attr("transform", "translate(14," + (margins.top + chartHeight / 2) + ") rotate(-90)")
      .attr("text-anchor", "middle")
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "11px")
      .text(props.yAxisLabel);

    mySvg
      .select("#xAxisLabel" + myClass)
      .attr("visibility", empty ? "hidden" : "visible")
      .attr("x", margins.left + chartWidth / 2)
      .attr("y", margins.top + chartHeight + 46)
      .attr("text-anchor", "middle")
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "13px")
      .text(props.xAxisLabel);

    // ── Clip paths ──
    mySvg
      .select("#brushClipRect" + myClass)
      .attr("x", 0)
      .attr("y", -margins.top)
      .attr("width", chartWidth)
      .attr("height", margins.top + chartHeight);
    mySvg
      .select("#xAxisClipRect" + myClass)
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", chartWidth)
      .attr("height", margins.mid);
    mySvg.select("#chartGroup" + myClass).attr("clip-path", "url(#brushClip" + myClass + ")");

    // ── Brush (x-axis zoom) ──
    let startingVisibleRows = Math.floor(chartWidth / 80);
    startingVisibleRows = Math.min(startingVisibleRows, rows.length);
    const brushStartX = empty ? 0 : (1 - startingVisibleRows / rows.length) * chartWidth;

    const brush: any = d3
      .brushX()
      .handleSize(10)
      .extent([
        [0, 0],
        [chartWidth, margins.brush],
      ])
      .on("start brush end", brushed);

    const brushGroup = mySvg.select("#brushGroup" + myClass);
    brushGroup.selectAll(".brushMiniBar" + myClass).remove();

    // The brush is the shared line chart's (shared/charts/line-chart): a
    // full-colour miniature of the whole chart, on no track of its own — the bars
    // here are drawn with the main chart's own geometry, scaled into the strip.
    const yScaleBrush = d3.scaleLinear().domain([0, yMax]).range([margins.brush, 0]);
    const brushGroupWidth = rows.length > 0 ? chartWidth / rows.length : 0;
    const brushBarArea = Math.min(brushGroupWidth * (1 - 0.35), 56);
    const brushGroupPad = (brushGroupWidth - brushBarArea) / 2;

    stackData.forEach((item) => {
      item.segments.forEach((seg) => {
        brushGroup
          .insert("rect", ":first-child")
          .attr("class", "brushMiniBar" + myClass)
          .attr("x", xScaleAll(item.index) + brushGroupPad)
          .attr("y", yScaleBrush(seg.y1))
          .attr("width", Math.max(brushBarArea, 1))
          .attr("height", Math.max(yScaleBrush(seg.y0) - yScaleBrush(seg.y1), 0.5))
          .attr("fill", seg.solid);
      });
    });

    brushGroup
      .attr(
        "transform",
        "translate(" + margins.left + "," + (margins.top + chartHeight + margins.mid) + ")"
      )
      .attr("visibility", empty ? "hidden" : "visible")
      .call(brush)
      .call(brush.move, [brushStartX, chartWidth]);

    // Line chart's brush styling: transparent overlay, grey selection.
    brushGroup.select(".overlay").style("fill", "transparent");
    brushGroup
      .selectAll(".selection")
      .attr("fill", BRUSH_FILL)
      .attr("fill-opacity", "0.2")
      .style("stroke", BRUSH_STROKE)
      .style("stroke-width", "1");

    mySvg
      .selectAll(".handleLines" + myClass)
      .attr("visibility", empty ? "hidden" : "visible")
      .attr("y1", margins.top + chartHeight + margins.mid + (margins.brush - 12) / 2)
      .attr("y2", margins.top + chartHeight + margins.mid + 12 + (margins.brush - 12) / 2)
      .style("stroke", "#8A98AB")
      .style("stroke-width", "1")
      .attr("transform", "translate(" + margins.left + ",0)");

    drawChart();

    function brushed(event: any): void {
      const selection = event.selection;
      if (selection === null) return;
      xScale.domain(selection.map(xScaleAll.invert));
      drawChart();

      const bg = mySvg.select("#brushGroup" + myClass);
      const handleEastX = +bg.select(".handle--e").attr("x");
      const handleWestX = +bg.select(".handle--w").attr("x");
      mySvg.select(".handleLeftLine1" + myClass).attr("x1", handleEastX + 3).attr("x2", handleEastX + 3);
      mySvg.select(".handleLeftLine2" + myClass).attr("x1", handleEastX + 7).attr("x2", handleEastX + 7);
      mySvg.select(".handleRightLine1" + myClass).attr("x1", handleWestX + 3).attr("x2", handleWestX + 3);
      mySvg.select(".handleRightLine2" + myClass).attr("x1", handleWestX + 7).attr("x2", handleWestX + 7);
      bg.selectAll(".handle")
        .attr("fill", "white")
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("y", (margins.brush - 24) / 2)
        .attr("height", 24)
        .style("filter", "url(#drop-shadow-brush-" + myClass + ")");
    }

    function updateYAxis(): void {
      mySvg
        .select("#yAxis" + myClass)
        .call(
          d3
            .axisLeft(yScale)
            .ticks(5)
            .tickSizeOuter(0)
            .tickFormat((d: any) => myChart.tickFormat(d))
        )
        .attr("transform", "translate(" + margins.left + "," + margins.top + ")")
        .attr("visibility", empty ? "hidden" : "visible");
      mySvg.select("#yAxis" + myClass + " path").style("stroke", "none");
      mySvg
        .selectAll(".axis" + myClass + " text")
        .style("font-weight", 500)
        .style("font-family", FONT)
        .style("font-size", "11px")
        .style("fill", TICK_LABEL);
      mySvg
        .selectAll("#yAxis" + myClass + " line")
        .attr("x1", 0)
        .attr("x2", chartWidth)
        .style("stroke", GRID_LINE)
        .style("stroke-dasharray", "4,4")
        .style("stroke-width", 1);
    }

    function drawChart(): void {
      const visibleGroups = xScale.domain()[1] - xScale.domain()[0];
      const groupWidth = chartWidth / visibleGroups;
      // The reference keeps the columns slim (~55px) however wide the card is.
      const barWidth = Math.min(groupWidth * (1 - 0.35), 56);
      const groupPad = (groupWidth - barWidth) / 2;

      updateYAxis();

      const chartGroup = mySvg.select("#chartGroup" + myClass);
      chartGroup.selectAll("*").remove();
      chartGroup.attr("transform", "translate(" + margins.left + "," + margins.top + ")");

      const xAxisGroup = mySvg.select("#xAxis" + myClass);
      xAxisGroup.selectAll("*").remove();
      if (empty) return;
      xAxisGroup
        .append("line")
        .attr("x1", 0)
        .attr("x2", chartWidth)
        .attr("y1", 0)
        .attr("y2", 0)
        .style("stroke", AXIS_LINE);

      // Each label opens its bar, as a click on the bar does — the label is the
      // easier target on a short or hidden-down bar.
      const xDomain = xScale.domain();
      rows.forEach((row, i) => {
        if (i >= Math.floor(xDomain[0]) && i < Math.ceil(xDomain[1])) {
          xAxisGroup
            .append("text")
            .attr("x", xScale(i) + groupWidth / 2)
            .attr("y", 6)
            .attr("dy", "1em")
            .style("text-anchor", "middle")
            .style("cursor", "pointer")
            .attr("fill", TICK_LABEL)
            .attr("font-weight", "500")
            .attr("font-size", "11px")
            .attr("font-family", FONT)
            .each(function (this: SVGTextElement) {
              fitAxisLabel(this, String(row.name), groupWidth);
            })
            .on("mouseover", function (this: SVGTextElement) {
              d3.select(this).attr("fill", SLIDER_ACCENT).style("text-decoration", "underline");
            })
            .on("mouseout", function (this: SVGTextElement) {
              d3.select(this).attr("fill", TICK_LABEL).style("text-decoration", null);
            })
            .on("click", () => {
              myChart.hideTooltip();
              myChart.showWaterfall(i);
            });
        }
      });

      stackData.forEach((item) => {
        const g = chartGroup
          .append("g")
          .attr("class", "spColumn" + myClass)
          .attr("transform", "translate(" + (xScale(item.index) + groupPad) + ",0)")
          .style("cursor", "pointer");

        item.segments.forEach((seg) => {
          const [domLo, domHi] = yScale.domain();
          const top = yScale(Math.min(seg.y1, domHi));
          const bottom = yScale(Math.max(seg.y0, domLo));
          if (bottom <= top) return;
          g.append("rect")
            .attr("x", 0)
            .attr("y", top)
            .attr("width", barWidth)
            .attr("height", Math.max(bottom - top, 0.5))
            .attr("fill", "url(#" + seg.gradId + ")");
        });

        // Hovering a column shows its full breakdown; a click opens it.
        g.on("mouseover", (event: MouseEvent) =>
          myChart.showTooltip("bar", { row: item.row }, event)
        )
          .on("mouseout", () => myChart.hideTooltip())
          .on("click", () => myChart.showWaterfall(item.index));
      });
    }

    this.createRangeSlider(margins, chartHeight, yScale, yMax, drawChart);
  }

  // ══════════════════════════════════════════════════════
  // WATERFALL VIEW — Staircase with Extension Boxes
  // ══════════════════════════════════════════════════════
  private plotWaterfallChart(): void {
    const myChart = this;
    const myClass = myChart.divId;
    const props = myChart.config;
    const mySvg: any = d3.select("#svg_" + myClass);
    mySvg.attr("width", "100%");
    const containerWidth: number = (mySvg.node() as HTMLElement).getBoundingClientRect().width;

    // Bars-view-only parts
    mySvg.select("#brushGroup" + myClass).attr("visibility", "hidden");
    mySvg.selectAll(".handleLines" + myClass).attr("visibility", "hidden");
    mySvg.select("#xAxis" + myClass).attr("visibility", "hidden");
    mySvg.select("#xAxisLabel" + myClass).attr("visibility", "hidden");
    mySvg.select("#noDataMessage" + myClass).text("");
    mySvg.select("#chartGroup" + myClass).attr("clip-path", null);

    const idx = myChart.waterfallIndex;
    if (idx === null) return;
    const row = myChart.data[idx];
    if (!row) return;
    const segs = myChart.getSegments(row, myChart.visibleKeys());
    const shownTotal = myChart.sumOf(row, myChart.visibleKeys());
    const N = segs.length;

    const height = props.chartHeight;
    const margins = { left: 64, right: 80, top: 30, bottom: 6, mid: 62, brush: 52 };
    const chartHeight = height - margins.top - margins.bottom - margins.mid - margins.brush;
    const barW = 44;
    const colGap = 5;
    const col0x = 20;

    const colX = [col0x];
    for (let i = 0; i < N; i++) colX.push(col0x + (i + 1) * (barW + colGap));
    // Extension boxes take whatever width the card has left (never under 260px).
    const extBoxW = Math.max(
      260,
      containerWidth - margins.left - margins.right - (colX[N] + barW + 10)
    );
    const borderX = colX[N] + barW + 10 + extBoxW;
    const svgWidth = Math.max(borderX + margins.right + margins.left, containerWidth);

    mySvg.attr("width", svgWidth).attr("height", height);

    const abs = myChart.isAbs;
    const yMax = myChart.yMaxFor(shownTotal);
    const yScale: any = d3
      .scaleLinear()
      .domain([(myChart.lineMin * yMax) / 100, (myChart.lineMax * yMax) / 100])
      .range([chartHeight, 0]);

    mySvg
      .select("#yAxisLabel" + myClass)
      .attr("visibility", "visible")
      .attr("transform", "translate(14," + (margins.top + chartHeight / 2) + ") rotate(-90)")
      .attr("text-anchor", "middle")
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "11px")
      .text(props.yAxisLabel);

    const updateAxes = () => {
      mySvg
        .select("#yAxis" + myClass)
        .attr("visibility", "visible")
        .call(
          d3
            .axisLeft(yScale)
            .ticks(10)
            .tickSizeOuter(0)
            .tickFormat((d: any) => myChart.tickFormat(d))
        )
        .attr("transform", "translate(" + margins.left + "," + margins.top + ")");
      mySvg.select("#yAxis" + myClass + " path").style("stroke", "none");
      mySvg
        .selectAll(".axis" + myClass + " text")
        .style("font-weight", 500)
        .style("font-family", FONT)
        .style("font-size", "10px")
        .style("fill", TICK_LABEL);
      mySvg
        .selectAll("#yAxis" + myClass + " line")
        .attr("x1", 0)
        .attr("x2", svgWidth - margins.left - margins.right)
        .style("stroke", GRID_LINE)
        .style("stroke-dasharray", "4,4")
        .style("stroke-width", 1);
    };

    const getSegLayout = () => {
      const dom = yScale.domain();
      let cum = 0;
      return segs.map((s, i) => {
        const y0 = cum;
        cum += abs ? s.count : s.pct * 100;
        const y1 = cum;
        const visY0 = Math.max(y0, dom[0]);
        const visY1 = Math.min(y1, dom[1]);
        const visible = visY1 > visY0;
        const topY = visible ? yScale(visY1) : 0;
        const botY = visible ? yScale(visY0) : 0;
        return { ...s, y0, y1, topY, botY, h: visible ? botY - topY : 0, colIdx: i + 1, visible };
      });
    };

    const drawWaterfall = () => {
      updateAxes();
      const segLayout = getSegLayout();
      const chartGroup = mySvg.select("#chartGroup" + myClass);
      chartGroup.selectAll("*").remove();
      chartGroup.attr("transform", "translate(" + margins.left + "," + margins.top + ")");

      // ── Stacked bar (column 0) ──
      const stackGroup = chartGroup.append("g");
      stackGroup
        .append("text")
        .attr("x", col0x + barW / 2)
        .attr("y", -10)
        .attr("text-anchor", "middle")
        .attr("font-family", FONT)
        .attr("font-size", "13px")
        .attr("font-weight", "700")
        .attr("fill", COUNT_LABEL)
        .text(myChart.fmt(shownTotal));

      segLayout
        .filter((s) => s.visible)
        .forEach((s) => {
          stackGroup
            .append("rect")
            .attr("x", col0x)
            .attr("y", s.topY)
            .attr("width", barW)
            .attr("height", Math.max(s.h, 0.5))
            .attr("fill", "url(#" + s.gradId + ")")
            .style("cursor", "pointer")
            .on("mouseover", (event: MouseEvent) =>
              myChart.showTooltip("segment", { row, seg: s }, event)
            )
            .on("mouseout", () => myChart.hideTooltip());
        });

      stackGroup
        .append("text")
        .attr("x", col0x + barW / 2)
        .attr("y", chartHeight + 16)
        .attr("text-anchor", "middle")
        .attr("font-family", FONT)
        .attr("font-size", "10px")
        .attr("font-weight", "700")
        .attr("fill", COUNT_LABEL)
        .text("Total");

      const connGroup = chartGroup.append("g");

      // ── Step bars + extension boxes ──
      segLayout
        .filter((s) => s.visible)
        .forEach((s) => {
          const stepX = colX[s.colIdx];
          const segH = Math.max(s.h, 2);
          const segGroup = chartGroup.append("g");

          const connL = col0x + barW;
          if (stepX - connL > 0) {
            [s.topY, s.topY + s.h].forEach((y: number) => {
              connGroup
                .append("line")
                .attr("x1", connL)
                .attr("y1", y)
                .attr("x2", stepX)
                .attr("y2", y)
                .attr("stroke", s.solid)
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", "4,3")
                .attr("opacity", 0.35);
            });
          }

          segGroup
            .append("rect")
            .attr("x", stepX)
            .attr("y", s.topY)
            .attr("width", barW)
            .attr("height", segH)
            .attr("fill", "url(#" + s.gradId + ")")
            .style("cursor", "pointer")
            .on("mouseover", (event: MouseEvent) =>
              myChart.showTooltip("segment", { row, seg: s }, event)
            )
            .on("mouseout", () => myChart.hideTooltip());

          const extLeft = stepX + barW + 4;
          const extW = Math.max(borderX - colX[s.colIdx] - barW - 4 - margins.left, 0);

          if (s.h >= 12) {
            const extGroup = segGroup.append("g");
            extGroup
              .append("rect")
              .attr("x", extLeft)
              .attr("y", s.topY)
              .attr("width", extW)
              .attr("height", segH)
              .attr("fill", s.extBg)
              .attr("stroke", s.extBorder)
              .attr("stroke-width", 1)
              .style("cursor", "pointer")
              .on("mouseover", (event: MouseEvent) =>
                myChart.showTooltip("segment", { row, seg: s }, event)
              )
              .on("mouseout", () => myChart.hideTooltip());

            extGroup
              .append("rect")
              .attr("x", extLeft + extW)
              .attr("y", s.topY)
              .attr("width", 5)
              .attr("height", segH)
              .attr("fill", s.accent);

            if (s.h >= 18) {
              const midY = s.topY + segH / 2;
              const fontSize = s.h >= 30 ? 12 : 10;
              const countSize = s.h >= 30 ? 14 : 11;

              extGroup
                .append("circle")
                .attr("cx", extLeft + 10)
                .attr("cy", midY)
                .attr("r", s.h >= 30 ? 3.5 : 2.5)
                .attr("fill", s.solid);

              const textEl = extGroup
                .append("text")
                .attr("x", extLeft + 20)
                .attr("y", midY + 4)
                .attr("font-family", FONT)
                .attr("font-size", fontSize + "px")
                .attr("font-weight", "500")
                .attr("fill", "#4b5563")
                .attr("pointer-events", "none");
              textEl.append("tspan").text(s.label + "  ");
              textEl
                .append("tspan")
                .attr("font-size", countSize + "px")
                .attr("font-weight", "700")
                .attr("fill", COUNT_LABEL)
                .text(myChart.fmt(s.count));
            }
          }

          if (s.h >= 16) {
            const badgeX = extLeft + extW + 14;
            const badgeY = s.topY + segH / 2;
            segGroup
              .append("rect")
              .attr("x", badgeX)
              .attr("y", badgeY - 9)
              .attr("width", 38)
              .attr("height", 18)
              .attr("rx", 4)
              .attr("fill", s.accent);
            segGroup
              .append("text")
              .attr("x", badgeX + 19)
              .attr("y", badgeY + 4)
              .attr("text-anchor", "middle")
              .attr("font-family", FONT)
              .attr("font-size", "10px")
              .attr("font-weight", "700")
              .attr("fill", "#fff")
              .text(s.pctRound + "%");
          }

          segGroup
            .append("text")
            .attr("x", stepX + barW / 2)
            .attr("y", chartHeight + 16)
            .attr("text-anchor", "middle")
            .attr("font-family", FONT)
            .attr("font-size", "8px")
            .attr("font-weight", "700")
            .attr("fill", s.solid)
            .text(s.shortLabel);
        });

      // ── Summary ──
      const summaryText = chartGroup
        .append("text")
        .attr("x", colX[N] + barW + 30)
        .attr("y", chartHeight + 40)
        .attr("font-family", FONT)
        .attr("font-size", "11px")
        .attr("fill", "#9ca3af");
      summaryText.append("tspan").text("Total " + props.unit + ": ");
      summaryText
        .append("tspan")
        .attr("font-size", "13px")
        .attr("font-weight", "700")
        .attr("fill", COUNT_LABEL)
        .text(myChart.fmt(shownTotal) + " ");
      summaryText.append("tspan").text("of " + myChart.fmt(Number(row.total)) + " ");
      summaryText
        .append("tspan")
        .attr("font-weight", "600")
        .attr("fill", SLIDER_ACCENT)
        .text(
          "(" + (Number(row.total) ? Math.round((shownTotal / Number(row.total)) * 100) : 0) + "%)"
        );
    };

    this.createRangeSlider(margins, chartHeight, yScale, yMax, drawWaterfall);
    drawWaterfall();
  }
}
